import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { BriefcaseBusiness, ChevronDown, Eye, Home, ShieldCheck } from "lucide-react";
import { getSessionInfo } from "@/lib/session.functions";
import { canUseWorkspace, workspaceForPath, type WorkspaceView } from "@/lib/workspace-view";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const VIEWS: Array<{
  key: WorkspaceView;
  label: string;
  to: "/customer" | "/partner" | "/owner";
  icon: typeof Home;
}> = [
  { key: "customer", label: "Customer", to: "/customer", icon: Home },
  { key: "partner", label: "Partner", to: "/partner", icon: BriefcaseBusiness },
  { key: "owner", label: "Owner", to: "/owner", icon: ShieldCheck },
];

export function WorkspaceSwitcher({ fallbackLabel }: { fallbackLabel: string }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: session } = useQuery({
    queryKey: ["session-info"],
    queryFn: () => getSessionInfo(),
    retry: false,
  });

  if (!session) {
    return (
      <span className="rounded-full bg-primary-soft px-3 py-1.5 text-xs font-bold text-primary">
        {fallbackLabel}
      </span>
    );
  }

  const availableViews = VIEWS.filter((view) => canUseWorkspace(view.key, session));
  if (availableViews.length === 1) {
    return (
      <span className="rounded-full bg-primary-soft px-3 py-1.5 text-xs font-bold text-primary">
        {fallbackLabel}
      </span>
    );
  }

  const current = workspaceForPath(pathname);
  const currentLabel = VIEWS.find((view) => view.key === current)?.label ?? "Customer";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex min-h-10 items-center gap-2 rounded-full border border-gold/35 bg-gold/10 px-3 text-xs font-bold text-foreground"
        aria-label="Switch Shields workspace"
      >
        <Eye className="h-4 w-4 text-gold" aria-hidden />
        <span>{currentLabel}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <span className="block">Switch Shields app</span>
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
            {session.isOwner
              ? "Customer, Partner and Owner stay separate."
              : "Customer and Partner tools stay separate."}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableViews.map((view) => (
          <DropdownMenuItem key={view.key} asChild>
            <Link to={view.to} className="flex min-h-10 items-center gap-2">
              <view.icon className="h-4 w-4" aria-hidden />
              {view.label}
              {current === view.key ? (
                <span className="ml-auto text-xs font-semibold text-success">Viewing</span>
              ) : null}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
